import { CommonModule } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
} from "@angular/core";
import {
    BehaviorSubject,
    debounceTime,
    delay,
    filter,
    fromEvent,
    interval,
    merge,
    Observable,
    Subject,
    Subscription,
    take,
    takeUntil,
    tap,
    throttle,
    withLatestFrom,
} from "rxjs";

import { LayoutFacade } from "@hermes/aphrodite/layout";
import { CurrencyModule } from "@hermes/aphrodite/price";
import { SvgIconComponent } from "@hermes/aphrodite/svg";
import { PriceRange } from "@hermes/states/grid-filters-state";
import { DOWN, LEFT, RIGHT, UP } from "@hermes/utils-generic/constants";
import { SrOnlyNoSnippetDirective } from "@hermes/utils-generic/directives/sr-only-no-snippet";

export enum Bound {
    MIN = "min",
    MAX = "max",
}

// Throttle to avoid overflow of event fired (mousemove).
export const MOUSE_MOVE_THROTTLE_TIME = 50;
// Debounce to avoid immediate treatment of event or overflowing event treatments.
export const DEBOUNCE_TIME = 500;

/**
 * This component implement a price slider to select a range of price.
 * Usage:
 * ```
 * <h-price-slider [priceRange]="priceRange" [isExpanded]="true" (valueChange)="onValueChange($event)"></h-price-slider>
 * ```
 */
@Component({
    standalone: true,
    imports: [
        CommonModule,
        CurrencyModule,
        SvgIconComponent,
        SrOnlyNoSnippetDirective,
    ],
    selector: "h-price-slider",
    templateUrl: "./price-slider.component.html",
    styleUrls: ["./price-slider.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PriceSliderComponent
    implements AfterViewInit, OnChanges, OnDestroy
{
    // direct access to html node element on html component template
    @ViewChild("rail", { static: true, read: ElementRef })
    public rail!: ElementRef;
    @ViewChild("min", { static: true, read: ElementRef })
    public min!: ElementRef;
    @ViewChild("max", { static: true, read: ElementRef })
    public max!: ElementRef;
    @ViewChild("selected", { static: true, read: ElementRef })
    public selected!: ElementRef;

    @Input()
    public isExpanded!: boolean;

    @Input()
    public priceRange!: PriceRange;

    @Output()
    public valueChange = new EventEmitter<PriceRange>();

    // value of the range price
    public valueMin = 0;
    public valueMinCurrent = 0;
    public ariaMinCurrent = 0;
    public valueMaxCurrent = 0;
    public ariaMaxCurrent = 0;
    public valueMax = 0;
    public valueChanged = "min";
    public handleEventOnRailSubject = new Subject<Event>();
    public handleEventOnRail$ = this.handleEventOnRailSubject.asObservable();
    public showSlider = new BehaviorSubject<boolean>(false);
    public showSlider$ = this.showSlider.asObservable();

    private subscription = new Subscription();
    // Variable used to track whether a key is pressed to prevent unnecessary grid refreshes during continuous key presses
    private keyPressed: boolean = false;
    // All keyboard actions with directions
    private keyboardActions: Map<string, number> = new Map([
        [RIGHT as string, 1],
        [LEFT as string, -1],
        [UP as string, 1],
        [DOWN as string, -1],
    ]);

    constructor(
        private cd: ChangeDetectorRef,
        public layoutFacade: LayoutFacade,
    ) {}

    public ngAfterViewInit(): void {
        this.initSubscriptions();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (
            !changes["priceRange"] ||
            this.arePriceRangesEquals(
                changes["priceRange"].previousValue,
                changes["priceRange"].currentValue,
            )
        ) {
            return;
        }
        this.initSlider();
    }

    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    public initSlider(): void {
        // if those params exist use it
        if (this.priceRange) {
            const { valueMin, valueMinCurrent, valueMaxCurrent, valueMax } =
                this.priceRange;

            this.valueMin = valueMin;
            this.valueMinCurrent =
                valueMinCurrent < valueMin || valueMinCurrent > valueMaxCurrent
                    ? valueMin
                    : valueMinCurrent;

            this.valueMaxCurrent =
                valueMaxCurrent < valueMinCurrent || valueMaxCurrent > valueMax
                    ? valueMax
                    : valueMaxCurrent;
            this.valueMax = valueMax;
        }

        this.updateAriaValues();
        this.showSlider.next(this.valueMin !== this.valueMax);

        this.updateBound();
    }

    public initSubscriptions(): void {
        // register keyboards events
        this.subscription.add(
            (
                merge(
                    fromEvent(this.min.nativeElement, "keydown"),
                    fromEvent(this.max.nativeElement, "keydown"),
                ) as Observable<KeyboardEvent>
            )
                .pipe(
                    withLatestFrom(this.showSlider$),
                    filter(([_, showSlider]) => Boolean(showSlider)),
                    tap(([event]) => this.handleKeyDown(event)),
                    debounceTime(DEBOUNCE_TIME),
                    // Emit changes only after the key is released
                    filter(() => !this.keyPressed),
                )
                .subscribe(() => this.emitValueChange()),
        );

        this.subscription.add(
            merge(
                fromEvent<KeyboardEvent>(this.min.nativeElement, "keyup"),
                fromEvent<KeyboardEvent>(this.max.nativeElement, "keyup"),
            )
                .pipe(
                    withLatestFrom(this.showSlider$),
                    filter(
                        ([event, showSlider]) =>
                            Boolean(showSlider) &&
                            [...this.keyboardActions.keys()].includes(
                                event.code,
                            ),
                    ),
                )
                // set pressed to false on keyup event
                .subscribe(() => (this.keyPressed = false)),
        );

        // register touch and mouse events
        this.subscription.add(
            (
                merge(
                    fromEvent(this.min.nativeElement, "mousedown"),
                    fromEvent(this.max.nativeElement, "mousedown"),
                    fromEvent(this.min.nativeElement, "touchstart"),
                    fromEvent(this.max.nativeElement, "touchstart"),
                ) as Observable<MouseEvent | TouchEvent>
            )
                .pipe(
                    withLatestFrom(this.showSlider$),
                    filter(([_, showSlider]) => Boolean(showSlider)),
                )
                .subscribe(([event]) =>
                    this.handleMouseDownOrTouchStart(
                        event.target as HTMLElement,
                    ),
                ),
        );

        this.subscription.add(
            this.handleEventOnRail$
                .pipe(
                    withLatestFrom(this.showSlider$),
                    filter(([_, showSlider]) => Boolean(showSlider)),
                    tap(([$event]) =>
                        this.handleRailClick($event as MouseEvent | TouchEvent),
                    ),
                    delay(DEBOUNCE_TIME),
                )
                .subscribe(() => this.emitValueChange()),
        );
    }

    /**
     * On rail click, the nearest bound have to move on mouse click event
     */
    public handleRailClick($event: MouseEvent | TouchEvent): void {
        // 1. get percentages of the click on the rail
        const clickPercent = this.getPercentOfBound($event);
        const minPercentage = this.getPercentOfBound($event, Bound.MIN);
        const maxPercentage = this.getPercentOfBound($event, Bound.MAX);

        // 2. who the nearest bound
        const distanceToMin = Math.abs(clickPercent - minPercentage);
        const distanceToMax = Math.abs(clickPercent - maxPercentage);
        // get minimal distance
        const result = Math.min(distanceToMin, distanceToMax);
        // get the bound to move
        const boundToMove = result === distanceToMin ? Bound.MIN : Bound.MAX;

        // 3. move that bound
        // new bound price value
        const newValue = this.getRailValue($event);
        // calculate the step according to the bound to move
        let step;
        if (boundToMove === Bound.MIN) {
            step = newValue - this.valueMinCurrent;
        }
        if (boundToMove === Bound.MAX) {
            step = newValue - this.valueMaxCurrent;
        }

        if (step) {
            // 4. move slider
            this.moveSlider(
                this[boundToMove].nativeElement as HTMLImageElement,
                step,
            );
        }
    }

    /**
     * calculates the percentage of the amount in relation to the quantity
     */
    public getPercentOfBound(
        $event: MouseEvent | TouchEvent,
        bound?: Bound,
    ): number {
        let position: number;
        const { offsetWidth } = this.rail.nativeElement;
        // get bound position if any
        if (bound) {
            position = this[bound].nativeElement.offsetLeft;
            // return rail position percentage of given bound
            return Math.round((position / offsetWidth) * 100);
        }
        // else get the X position in relation to slider width
        const offsetLeft = Math.floor(
            this.rail.nativeElement.getBoundingClientRect().left,
        );
        const diffX = this.getEventPageX($event) - offsetLeft;
        return Math.round((diffX / offsetWidth) * 100);
    }

    public emitValueChange() {
        if (this.isApiCallUsefull()) {
            this.updateAriaValues();
            this.cd.markForCheck();
            const prices: PriceRange = {
                valueMin: this.valueMin,
                valueMinCurrent: this.valueMinCurrent,
                valueMaxCurrent: this.valueMaxCurrent,
                valueMax: this.valueMax,
                valueChanged: this.valueChanged,
            };
            this.valueChange.emit(prices);
        }
    }

    /**
     * direct acces to the dom to update position
     */
    public updateBound(): void {
        if (this.showSlider.value) {
            // define pourcentage value for bounds
            const valueMinCurrentP = this.getPercentage(this.valueMinCurrent);
            const valueMaxCurrentP = this.getPercentage(this.valueMaxCurrent);
            // update bounds
            this.min.nativeElement.style.left = `${valueMinCurrentP}%`;
            this.max.nativeElement.style.left = `${valueMaxCurrentP}%`;
            // update background
            this.selected.nativeElement.style.left = `${valueMinCurrentP}%`;
            this.selected.nativeElement.style.right = `${
                100 - valueMaxCurrentP
            }%`;
        }
    }

    public moveSlider(
        node: HTMLElement,
        value: number,
        keyName?: string,
    ): void {
        let update = false;
        switch (node.id) {
            case Bound.MIN: {
                update = true;
                // update current min value
                this.valueMinCurrent = this.updateMin(value, keyName);
                break;
            }
            case Bound.MAX: {
                update = true;
                // update current max value
                this.valueMaxCurrent = this.updateMax(value, keyName);
                break;
            }
            default: {
                break;
            }
        }
        if (update) {
            this.updateBound();
        }
        this.cd.markForCheck();
    }

    public updateMin(value: number, keyName?: string): number {
        // new current min value
        const newMinValue = this.valueMinCurrent + value;
        // Change default id to focus after refreshing.
        this.valueChanged = Bound.MIN;
        // if home key or negative new value
        if (keyName === "home" || newMinValue <= this.valueMin) {
            // return min value
            return this.valueMin;
        }
        // if end key or superior to current max value
        if (keyName === "end" || newMinValue >= this.valueMaxCurrent) {
            // return current max value
            return this.valueMaxCurrent;
        }
        // finally, return new current min value
        return newMinValue;
    }

    public updateMax(value: number, keyName?: string): number {
        // new current max value
        const newMaxValue = this.valueMaxCurrent + value;
        // Change default id to focus after refreshing.
        this.valueChanged = Bound.MAX;
        // if end key or superior to max value
        if (keyName === "end" || newMaxValue > this.valueMax) {
            // return max value
            return this.valueMax;
        }
        if (keyName === "home" || newMaxValue <= this.valueMinCurrent) {
            // return current min value
            return this.valueMinCurrent;
        }
        // finally, return new current max value
        return newMaxValue;
    }

    public isApiCallUsefull() {
        return (
            this.ariaMinCurrent !== this.valueMinCurrent ||
            this.ariaMaxCurrent !== this.valueMaxCurrent ||
            this.valueMin !== this.priceRange.valueMin ||
            this.valueMax !== this.priceRange.valueMax ||
            this.valueMinCurrent !== this.priceRange.valueMinCurrent ||
            this.valueMaxCurrent !== this.priceRange.valueMaxCurrent
        );
    }

    private arePriceRangesEquals(
        previousPriceRange: PriceRange,
        currentPriceRange: PriceRange,
    ): boolean {
        return (
            currentPriceRange?.valueMax === previousPriceRange?.valueMax &&
            currentPriceRange?.valueMin === previousPriceRange?.valueMin &&
            currentPriceRange?.valueMaxCurrent ===
                previousPriceRange?.valueMaxCurrent &&
            currentPriceRange?.valueMinCurrent ===
                previousPriceRange?.valueMinCurrent
        );
    }

    /**
     * Calc the percentage for a value between min and max values
     */
    private getPercentage(value: number): number {
        return Math.floor(
            ((value - this.valueMin) * 100) / (this.valueMax - this.valueMin),
        );
    }

    /* eslint-disable @typescript-eslint/naming-convention */
    private handleKeyDown = ($event: KeyboardEvent): void => {
        // 2% step
        const stepTwoPercent = Math.ceil((this.valueMax - this.valueMin) / 50);
        const direction = this.keyboardActions.get($event.key);

        if (direction) {
            this.moveSlider(
                $event.target as HTMLElement,
                direction * stepTwoPercent,
            );

            this.keyPressed = true;
            this.updateAriaValues();
        }
    };

    private handleMouseDownOrTouchStart(node: HTMLElement): void {
        const mouseUpOrTouchEnd$ = merge(
            fromEvent(document, "mouseup"),
            fromEvent(document, "touchend"),
        ).pipe(
            take(1),
            tap(() => this.emitValueChange()),
        );

        this.subscription.add(mouseUpOrTouchEnd$.subscribe());

        // Subscribe to mousemove only until mouseup is triggered.
        this.subscription.add(
            (
                merge(
                    fromEvent(document, "mousemove"),
                    fromEvent(document, "touchmove"),
                ) as Observable<TouchEvent | MouseEvent>
            )
                .pipe(
                    throttle(() => interval(MOUSE_MOVE_THROTTLE_TIME)),
                    tap((event: MouseEvent | TouchEvent) =>
                        this.handleMoveEvents(event, node),
                    ),
                    takeUntil(mouseUpOrTouchEnd$),
                )
                .subscribe(),
        );
    }

    private handleMoveEvents(
        $event: MouseEvent | TouchEvent,
        node: HTMLElement,
    ): void {
        // new bound price value
        const newValue = this.getRailValue($event);
        // calc step
        let step = newValue;
        switch (node.id) {
            case Bound.MIN: {
                step = newValue - this.valueMinCurrent;
                break;
            }
            case Bound.MAX: {
                step = newValue - this.valueMaxCurrent;
                break;
            }
            default: {
                break;
            }
        }
        // move bound to new value
        this.moveSlider(node, step);
    }

    private getRailValue($event: MouseEvent | TouchEvent): number {
        const { left, width } = this.rail.nativeElement.getBoundingClientRect();

        // get the X position in relation to slider width
        const diffX = this.getEventPageX($event) - left;

        // new bound price value
        return Math.floor(
            this.valueMin + ((this.valueMax - this.valueMin) * diffX) / width,
        );
    }

    private getEventPageX(event: MouseEvent | TouchEvent): number {
        if ((event as MouseEvent)["pageX"]) {
            return (event as MouseEvent).pageX;
        }
        if ((event as TouchEvent)["touches"]) {
            const touches: TouchList = (event as TouchEvent).touches;
            const lastTouch: Touch | null = touches.item(touches.length - 1);

            if (lastTouch) {
                return lastTouch.pageX;
            }
        }
        return 0;
    }

    private updateAriaValues() {
        this.ariaMinCurrent = this.valueMinCurrent;
        this.ariaMaxCurrent = this.valueMaxCurrent;
    }
}
